import { ViewportScroller } from '@angular/common';
import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';

import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ListItemDirective } from '~/core/directives/list-item.directive';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';

import { TimeslotListVariantEnum } from './enums/timeslot-list-variant.enum';
import { CtaButtonTypeEnum } from '../call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '../vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '../buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { Subscription } from 'rxjs';
import isBulgaria from '~/core/helpers/country/country.helper';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';

@Component({
  selector: 'app-activity-timeslot-list',
  templateUrl: './timeslot-list.component.html',
  styleUrls: ['./timeslot-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ActivityTimeslotListComponent implements OnDestroy, OnInit {
  @ContentChildren(ListItemDirective)
  public buttons: QueryList<ListItemDirective>;

  @Input() public activity: Activity;
  @Input() public hasLoadMoreButton: boolean;
  @Input() public hasPagination = false;
  @Input() public hasSeeMoreUrl = false;
  @Input() public seeMoreUrl: string;
  @Input() public timeslots: Timeslot[] = [];
  @Input() public organizerName: string;
  @Input() public readonly timeslotVariant: TimeslotListVariantEnum =
    TimeslotListVariantEnum.EXTENDED;

  private readonly subscriptions = new Subscription();

  @Output() public loadMore = new EventEmitter<void>();
  @Output() public selectTimeslot = new EventEmitter<void>();

  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly modalVariant: ModalVariantEnum = ModalVariantEnum.Full;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public isMobileDevice: boolean;
  public isTabletDevice: boolean;
  public isBg: boolean;

  constructor(
    private readonly matchMediaService: MatchMediaService,
    private readonly viewportScroller: ViewportScroller,
    public readonly customHeadTag: CustomHeadTagService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.hasLoadMoreButton = this.timeslots?.length >= 5;
    this.isBg = isBulgaria();
    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.setContentMeta();
  }

  public get isExternal(): boolean {
    return this.timeslots[0]?.getBookingURL !== undefined;
  }

  get containerModifier(): string {
    return this.isShort && 'activity-component__timeslot-container--short';
  }

  get isShort(): boolean {
    return this.timeslotVariant === TimeslotListVariantEnum.SHORT;
  }

  get timeslotSizeClass(): string {
    return this.isShort && this.timeslots?.length > 1 ? 'w-10/12' : 'flex-grow';
  }

  public goBackUp(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  public setContentMeta(): void {
    if (this.activity?.timeslots?.length === 0) {
      this.customHeadTag.addTag('meta', {
        name: 'robots',
        content: 'noindex, nofollow',
        id: 'content-meta',
      });
    }
  }
}
